import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentReturn as ReturnIcon,
  Warning as DamageIcon,
  Report as LossIcon,
  Build as MaintenanceIcon,
  Laptop as LaptopIcon,
  PhoneAndroid as PhoneIcon,
  DirectionsCar as CarIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const AssetList = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    assetType: '',
    status: '',
    condition: '',
    startDate: '',
    endDate: '',
    searchTerm: '',
    page: 1,
    limit: 50
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [employeeMap, setEmployeeMap] = useState({});

  // Fetch assets
  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-asset/list', filters);
      if (response.data?.success) {
        setAssets(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/employee-asset/stats/overview');
      if (response.data?.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch employees for avatars - use Redux store instead of API
  useEffect(() => {
    // Employee list is already loaded in Redux by Dashboard
    // Just skip this for now - employee names will show as IDs
    // TODO: Add proper employee API endpoint or use Redux selector
    fetchStats();
    fetchAssets();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [filters]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  // View asset details
  const handleView = (id) => {
    navigate(`/userdashboard/staff/hrm/assets/view/${id}`);
  };

  // Return asset
  const handleReturn = async (id) => {
    const result = await Swal.fire({
      title: 'Return Asset?',
      html: `
        <textarea id="returnNotes" class="swal2-textarea" placeholder="Return notes (optional)"></textarea>
        <select id="returnCondition" class="swal2-select">
          <option value="EXCELLENT">Excellent</option>
          <option value="GOOD">Good</option>
          <option value="FAIR">Fair</option>
          <option value="POOR">Poor</option>
          <option value="DAMAGED">Damaged</option>
        </select>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirm Return',
      preConfirm: () => {
        return {
          returnNotes: document.getElementById('returnNotes').value,
          returnCondition: document.getElementById('returnCondition').value
        };
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-asset/return/${id}`, result.value);
        if (response.data.success) {
          Swal.fire('Success', 'Asset returned successfully', 'success');
          fetchAssets();
          fetchStats();
        }
      } catch (error) {
        console.error('Error returning asset:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to return asset', 'error');
      }
    }
  };

  // Report damage
  const handleDamage = async (id) => {
    const result = await Swal.fire({
      title: 'Report Damage',
      html: `
        <textarea id="damageDescription" class="swal2-textarea" placeholder="Damage description *" required></textarea>
        <input id="damageAmount" class="swal2-input" type="number" placeholder="Recovery amount" />
      `,
      showCancelButton: true,
      confirmButtonText: 'Report Damage',
      confirmButtonColor: '#f44336',
      preConfirm: () => {
        const description = document.getElementById('damageDescription').value;
        if (!description) {
          Swal.showValidationMessage('Please enter damage description');
          return false;
        }
        return {
          damageDescription: description,
          damageAmount: parseFloat(document.getElementById('damageAmount').value) || 0
        };
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-asset/damage/${id}`, result.value);
        if (response.data.success) {
          Swal.fire('Success', 'Damage reported successfully', 'success');
          fetchAssets();
          fetchStats();
        }
      } catch (error) {
        console.error('Error reporting damage:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to report damage', 'error');
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      ASSIGNED: 'success',
      RETURNED: 'info',
      DAMAGED: 'error',
      LOST: 'error',
      UNDER_MAINTENANCE: 'warning'
    };
    return colors[status] || 'default';
  };

  // Get condition color
  const getConditionColor = (condition) => {
    const colors = {
      EXCELLENT: 'success',
      GOOD: 'success',
      FAIR: 'warning',
      POOR: 'error',
      DAMAGED: 'error'
    };
    return colors[condition] || 'default';
  };

  // Get asset icon
  const getAssetIcon = (type) => {
    const icons = {
      LAPTOP: <LaptopIcon />,
      MOBILE: <PhoneIcon />,
      TABLET: <PhoneIcon />,
      DESKTOP: <LaptopIcon />,
      VEHICLE: <CarIcon />,
      UNIFORM: <BadgeIcon />,
      ID_CARD: <BadgeIcon />,
      ACCESS_CARD: <BadgeIcon />
    };
    return icons[type] || <BadgeIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Asset Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/userdashboard/staff/hrm/assets/bulk-assign')}
          >
            Bulk Assignment
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/userdashboard/staff/hrm/assets/reports')}
          >
            Reports
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/userdashboard/staff/hrm/assets/create')}
          >
            Assign New Asset
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Total Assets</Typography>
                <Typography variant="h4">{stats.totalAssets}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Assigned</Typography>
                <Typography variant="h4" color="success.main">{stats.assignedAssets}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Returned</Typography>
                <Typography variant="h4" color="info.main">{stats.returnedAssets}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Damaged</Typography>
                <Typography variant="h4" color="error.main">{stats.damagedAssets}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Lost</Typography>
                <Typography variant="h4" color="error.main">{stats.lostAssets}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Maintenance</Typography>
                <Typography variant="h4" color="warning.main">{stats.underMaintenance}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Asset Type"
              value={filters.assetType}
              onChange={(e) => handleFilterChange('assetType', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="LAPTOP">Laptop</MenuItem>
              <MenuItem value="MOBILE">Mobile</MenuItem>
              <MenuItem value="TABLET">Tablet</MenuItem>
              <MenuItem value="DESKTOP">Desktop</MenuItem>
              <MenuItem value="VEHICLE">Vehicle</MenuItem>
              <MenuItem value="UNIFORM">Uniform</MenuItem>
              <MenuItem value="ID_CARD">ID Card</MenuItem>
              <MenuItem value="ACCESS_CARD">Access Card</MenuItem>
              <MenuItem value="TOOLS">Tools</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="ASSIGNED">Assigned</MenuItem>
              <MenuItem value="RETURNED">Returned</MenuItem>
              <MenuItem value="DAMAGED">Damaged</MenuItem>
              <MenuItem value="LOST">Lost</MenuItem>
              <MenuItem value="UNDER_MAINTENANCE">Under Maintenance</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Condition"
              value={filters.condition}
              onChange={(e) => handleFilterChange('condition', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Conditions</MenuItem>
              <MenuItem value="EXCELLENT">Excellent</MenuItem>
              <MenuItem value="GOOD">Good</MenuItem>
              <MenuItem value="FAIR">Fair</MenuItem>
              <MenuItem value="POOR">Poor</MenuItem>
              <MenuItem value="DAMAGED">Damaged</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Asset name, code, serial..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Assigned Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Condition</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : !assets || assets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No assets found
                </TableCell>
              </TableRow>
            ) : (
              assets.map((asset) => {
                const employee = employeeMap[asset.employeeId?._id] || asset.employeeId;
                return (
                  <TableRow key={asset._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getAssetIcon(asset.assetType)}
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {asset.assetName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {asset.assetCode}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          src={employee?.imageUrl}
                          sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                        >
                          {!employee?.imageUrl && employee?.staff_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {employee?.staff_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {employee?.emp_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(asset.assignedDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.status.replace(/_/g, ' ')}
                        color={getStatusColor(asset.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={asset.condition}
                        color={getConditionColor(asset.condition)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      ₹{asset.currentValue?.toLocaleString() || asset.purchaseValue?.toLocaleString() || 0}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(asset._id)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {asset.status === 'ASSIGNED' && (
                          <>
                            <Tooltip title="Return Asset">
                              <IconButton size="small" color="info" onClick={() => handleReturn(asset._id)}>
                                <ReturnIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Report Damage">
                              <IconButton size="small" color="error" onClick={() => handleDamage(asset._id)}>
                                <DamageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Asset Details</DialogTitle>
        <DialogContent>
          {selectedAsset && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Asset Type</Typography>
                <Typography variant="body1">{selectedAsset.assetType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Asset Name</Typography>
                <Typography variant="body1">{selectedAsset.assetName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Asset Code</Typography>
                <Typography variant="body1">{selectedAsset.assetCode}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Serial Number</Typography>
                <Typography variant="body1">{selectedAsset.serialNumber || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedAsset.status} color={getStatusColor(selectedAsset.status)} size="small" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Condition</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedAsset.condition} color={getConditionColor(selectedAsset.condition)} size="small" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Purchase Value</Typography>
                <Typography variant="body1">₹{selectedAsset.purchaseValue?.toLocaleString() || 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Current Value</Typography>
                <Typography variant="body1">₹{selectedAsset.currentValue?.toLocaleString() || 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Assigned Date</Typography>
                <Typography variant="body1">{new Date(selectedAsset.assignedDate).toLocaleDateString()}</Typography>
              </Grid>
              {selectedAsset.actualReturnDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Return Date</Typography>
                  <Typography variant="body1">{new Date(selectedAsset.actualReturnDate).toLocaleDateString()}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetList;
